import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaRobot,
  FaBars,
  FaComments,
  FaFileUpload,
  FaHome,
  FaTimes,
  FaUserEdit,
  FaUserFriends,
  FaHandsHelping
} from "react-icons/fa";

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: FaHome, path: "/worker-home" },
  { key: "profile", label: "Edit Profile", icon: FaUserEdit, path: "/remoteProfileEdit" },
  { key: "patients", label: "People I Support", icon: FaUserFriends, path: "/activatedpatients" },
  { key: "agents", label: "Agents", icon: FaRobot, path: "/worker-agents" },
  { key: "care", label: "Uber Care", icon: FaHandsHelping, path: "/activatedPatientMap" },
  { key: "documents", label: "Upload Documents", icon: FaFileUpload, path: null },
  { key: "elderchat", label: "ElderChat", icon: FaComments, path: null }
];

const WorkerSidebarShell = ({ activeItem = "dashboard", children }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 980);
  const [isMenuOpen, setIsMenuOpen] = useState(() => window.innerWidth >= 980);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 980;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(true);
      } else {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = item.key === activeItem;
    const isClickable = Boolean(item.path);

    return (
      <button
        key={item.key}
        type="button"
        onClick={() => {
          if (!item.path) return;
          navigate(item.path);
          if (isMobile) setIsMenuOpen(false);
        }}
        disabled={!isClickable}
        style={{
          ...navItem,
          ...(isMobile ? navItemMobile : null),
          ...(isActive ? activeNavItem : null),
          ...(isClickable ? null : disabledNavItem)
        }}
      >
        <Icon style={{ fontSize: "18px" }} />
        {item.label}
      </button>
    );
  };

  return (
    <div style={page}>
      <style>
        {`
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <div style={{ ...workspace, ...(isMobile ? workspaceMobile : null) }}>
        <aside style={{ ...sidebar, ...(isMobile ? sidebarMobile : null) }}>
          {isMobile ? (
            <>
              <div style={mobileMenuHeader}>
                <button
                  type="button"
                  style={hamburgerButton}
                  onClick={() => setIsMenuOpen((open) => !open)}
                  aria-label="Toggle menu"
                >
                  {isMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
                </button>
                <strong style={mobileMenuTitle}>Worker Menu</strong>
                <div style={{ width: 42 }} />
              </div>
              {isMenuOpen ? (
                <div style={mobileNavOverlay}>
                  <div style={mobileNavList}>{NAV_ITEMS.map(renderNavItem)}</div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <h2 style={sidebarTitle}>Worker Page</h2>
              {NAV_ITEMS.map(renderNavItem)}
            </>
          )}
        </aside>

        <main style={{ ...mainContent, ...(isMobile ? mainContentMobile : null) }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default WorkerSidebarShell;

const page = {
  minHeight: "100vh",
  background: "#f4f6f8",
  width: "100%",
  overflowX: "hidden",
  boxSizing: "border-box"
};

const workspace = {
  display: "flex",
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  position: "relative"
};

const workspaceMobile = {
  flexDirection: "column"
};

const sidebar = {
  width: "280px",
  background: "#0E5580",
  color: "#fff",
  padding: "32px 20px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  boxShadow: "4px 0 15px rgba(0,0,0,0.05)",
  zIndex: 10
};

const sidebarMobile = {
  width: "100%",
  padding: "12px 16px",
  gap: "0",
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 50,
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
};

const mobileMenuHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%"
};

const mobileMenuTitle = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#fff"
};

const hamburgerButton = {
  background: "transparent",
  border: "none",
  color: "#fff",
  padding: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "8px",
  transition: "background 0.2s"
};

const mobileNavOverlay = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "#0E5580",
  padding: "0 16px 20px",
  boxShadow: "0 10px 15px rgba(0,0,0,0.15)",
  borderBottomLeftRadius: "16px",
  borderBottomRightRadius: "16px",
  animation: "slideDown 0.2s ease-out forwards",
  transformOrigin: "top",
  zIndex: 40
};

const mobileNavList = {
  display: "grid",
  gap: "10px",
  marginTop: "10px"
};

const sidebarTitle = {
  margin: "0 0 24px",
  fontSize: "24px",
  fontWeight: 600,
  borderBottom: "2px solid rgba(255,255,255,0.1)",
  paddingBottom: "16px"
};

const navItem = {
  border: "none",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: "14px",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: 500,
  background: "transparent",
  whiteSpace: "nowrap",
  color: "#e2e8f0",
  textAlign: "left",
  transition: "all 0.2s ease"
};

const navItemMobile = {
  width: "100%",
  background: "rgba(255,255,255,0.05)"
};

const activeNavItem = {
  background: "rgba(255,255,255,0.15)",
  color: "#fff",
  fontWeight: 600,
  boxShadow: "inset 4px 0 0 #4EA5D9"
};

const disabledNavItem = {
  opacity: 0.5,
  cursor: "not-allowed"
};

const mainContent = {
  flex: 1,
  padding: "32px 40px 40px",
  overflowY: "auto",
  minWidth: 0,
  boxSizing: "border-box"
};

const mainContentMobile = {
  padding: "20px 16px 30px"
};
